// ExpenseBreakdown.tsx
import React from 'react';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';

import { FinanceData,Expense } from '../types';

// Register ChartJS components for Pie chart
ChartJS.register(ArcElement, Tooltip, Legend);

interface ExpenseBreakdownProps {
  financeData: FinanceData;
}

const ExpenseBreakdown: React.FC<ExpenseBreakdownProps> = ({ financeData }) => {
  // Aggregate expenses by category for the pie chart
  const categoryTotals = financeData.expenses.reduce((acc, exp: Expense) => {
    const category = exp.customCategory || exp.category;
    acc[category] = (acc[category] || 0) + exp.amount;
    return acc;
  }, {} as Record<string, number>);

  // Prepare pie chart data
  const pieData = {
    labels: Object.keys(categoryTotals),
    datasets: [
      {
        data: Object.values(categoryTotals),
        backgroundColor: [
          '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
          '#FF9F40', '#C9CB3F', '#E7E9ED', // Add more colors if needed
        ],
        hoverOffset: 4,
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'right' as const },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.label}: $${context.raw}`,
        },
      },
    },
  };

  // Total expenses for display
  const totalExpenses = financeData.expenses.reduce((sum, exp) => sum + exp.amount, 0);

  // CSV Export function
  const exportToCSV = () => {
    const headers = ['ID,Date,Category,Custom Category,Amount\n'];
    const rows = financeData.expenses.map((exp: Expense) =>
      `${exp.id},${exp.date},${exp.category},${exp.customCategory || ''},${exp.amount}`
    );
    const csvContent = headers.concat(rows).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${financeData.title}_expenses.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      {/* Header */}
      <h1 style={{ color: '#333', textAlign: 'center' }}>Expense Breakdown</h1>
      <p style={{ textAlign: 'center', color: '#666' }}>
        {new Date(financeData.startDate).toLocaleDateString()} - {new Date(financeData.endDate).toLocaleDateString()}
      </p>
      <p style={{ textAlign: 'center', fontWeight: 'bold' }}>Total Expenses: ${totalExpenses}</p>

      {/* Pie Chart */}
      <div style={{ maxWidth: '600px', margin: '20px auto' }}>
        <Pie data={pieData} options={pieOptions} />
      </div>

      {/* Expenses Table */}
      {/* <h2>All Expenses</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead>
          <tr style={{ background: '#f5f5f5' }}>
            <th style={{ padding: '10px', textAlign: 'left' }}>Date</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Category</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Custom Category</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {financeData.expenses.map((exp: Expense) => (
            <tr key={exp.id} style={{ borderBottom: '1px solid #ddd' }}>
              <td style={{ padding: '10px' }}>{exp.date}</td>
              <td style={{ padding: '10px' }}>{exp.category}</td>
              <td style={{ padding: '10px' }}>{exp.customCategory || '-'}</td>
              <td style={{ padding: '10px' }}>${exp.amount}</td>
            </tr>
          ))}
        </tbody>
      </table> */}

      {/* Export Button */}
      <button
        onClick={exportToCSV}
        style={{
          display: 'block',
          margin: '20px auto',
          padding: '10px 20px',
          background: '#28A745',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
      >
        Export to CSV
      </button>
    </div>
  );
};

export default ExpenseBreakdown;